'use client';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import BaseModal from '@c/modals/BaseModal';
import { ProcessStep, BProcess } from '@c/process/BaseProcess';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { getDirtBikeShipping } from '@util/firestore/products';
import useBikes from '@util/hooks/useBikes';
import { formatCurrency, isMobile } from '@util/index';
import { encodeQueryData } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { Make } from 'models/shared';
import { useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const ShipMyDirtBike = () => {
  const params = useSearchParams();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fromSignup = params?.get('fromSignup') === 'true';
    if (fromSignup) {
      setShowModal(true);
    }
  }, [params]);

  return (
    <div className="flex w-full flex-col">
      <Header setShowModal={setShowModal} />
      <Steps />
      <Benefits />
      <Footer setShowModal={setShowModal} />
      <GetAQuoteModal isOpen={showModal} dismiss={() => setShowModal(false)} />
    </div>
  );
};

const Header = ({ setShowModal }: { setShowModal: (val: boolean) => void }) => (
  <div className="relative flex h-[40rem] w-full flex-col  items-center gap-[3.2rem] bg-black px-[1rem] sm:items-start sm:gap-[2.4rem] sm:sm:px-[12rem] ">
    <SafeImage
      alt={'ship my dirt bike banner'}
      src={
        'https://cdn.mxlocker.com/assets/ship-my-dirt-bike/ship-my-dirt-bike-banner.webp'
      }
      fill
      className="object-cover"
    />
    <div className="z-10 flex h-full flex-col items-center justify-center gap-[1.6rem] text-white sm:items-start">
      <h1 className="text-[2.4rem] font-semibold lg:text-[4rem]">
        Ship My Dirt Bike
      </h1>
      <h3 className="flex flex-col items-center gap-[0.8rem] text-[1.6rem] font-semibold sm:flex-row">
        Ship Nationwide. In Partnership with
        <SafeImage
          src={'https://cdn.mxlocker.com/assets/ship-my-dirt-bike/uship.webp'}
          alt="u ship logo"
          height={28}
          width={55}
        />
      </h3>
      <Button
        type="secondary"
        onClick={() => setShowModal(true)}
        text="Get a Free Quote"
        width="fixed"
      />
    </div>
  </div>
);

const Steps = () => (
  <div className="mx-auto flex w-full max-w-[144rem] flex-col items-center gap-[6.4rem] py-[10rem] sm:px-[20rem]">
    <div className="flex flex-col items-center gap-[1.6rem] sm:px-[24rem]">
      <h3 className="text-[2.4rem] font-semibold sm:text-[3.2rem]">
        How it Works
      </h3>
      <span className=" text-[1.2rem] text-gray-400 sm:text-[1.6rem]">
        Easily learn how to ship your dirt bike with MX Locker.
      </span>
    </div>
    <div className="mx-auto flex w-full max-w-[144rem] flex-col justify-center sm:flex-row sm:gap-[2.4rem]">
      <div className="flex w-full flex-col gap-[2.4rem] p-[2.4rem]">
        <div className="flex h-[18rem] w-full items-center justify-center rounded-brand bg-gray-50">
          <SafeImage
            src={
              'https://cdn.mxlocker.com/assets/ship-my-dirt-bike/step-1-icon.webp'
            }
            height={82}
            width={132}
            alt="bike icon"
          />
        </div>
        <div className="flex w-full flex-col gap-[2.4rem]">
          <div className="flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full bg-brand-secondary font-semibold text-white">
            1
          </div>
          <strong>Share your bike details</strong>
          <span className="text-gray-400">
            Share your year, make, model and where it’s going. Whether it’s
            around the corner or across the country, we cover it all.
          </span>
        </div>
      </div>
      <div className="flex w-full flex-col gap-[2.4rem] p-[2.4rem]">
        <div className="flex h-[18rem] w-full items-center justify-center rounded-brand bg-gray-50">
          <CompareIcon />{' '}
        </div>
        <div className="flex w-full flex-col gap-[2.4rem]">
          <div className="flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full bg-brand-secondary font-semibold text-white">
            2
          </div>
          <strong>Compare estimates and carrier reviews</strong>
          <span className="text-gray-400">
            Your dirt bike shipping request will be sent to thousands of
            feedback-rated providers. Compare their rates, shipping history, and
            reviews.
          </span>
        </div>
      </div>
      <div className="flex w-full flex-col gap-[2.4rem] p-[2.4rem]">
        <div className="flex h-[18rem] w-full items-center justify-center rounded-brand bg-gray-50">
          <TrackIcon />
        </div>
        <div className="flex w-full flex-col gap-[2.4rem]">
          <div className="flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full bg-brand-secondary font-semibold text-white">
            3
          </div>
          <strong>Track the journey</strong>
          <span className="text-gray-400">
            Track its progress right in your dashboard. Your carrier will notify
            you when your item has been delivered.
          </span>
        </div>
      </div>
    </div>
  </div>
);

const Benefits = () => (
  <div className="flex w-full flex-col items-center justify-center gap-[4.8rem] bg-gray-100 px-[2rem] py-[10rem] sm:px-[20rem]">
    <div className="flex max-w-[144rem] flex-col items-center gap-[1.6rem] px-[1rem] sm:px-[24rem] ">
      <h3 className="text-[2.4rem] font-semibold sm:text-[3.2rem]">
        Why Choose Us?
      </h3>
      <span className="text-center text-[1.2rem] text-gray-500 sm:text-[1.6rem]">
        We know when it comes to shipping your bike you want the safest
        transport at the best rate. MX Locker partners with uShip to make it
        easy to find reliable transporters who compete for your business and
        track your shipment to its destination.
      </span>
    </div>
    {/* <div className="flex w-full max-w-[144rem] justify-between  gap-[2.4rem]">
        <div className="flex flex-col  gap-[2.4rem]">
          <ControlIcon />
          <strong>You&apos;re in control</strong>
          <span className="text-gray-500">
            MX Locker partnered with uShip, an open marketplace that connects you
            with customer-reviewed service providers.
          </span>
        </div>
        <div className="flex flex-col  gap-[2.4rem]">
          <ShipIcon />
          <strong>Ship with confidence</strong>
          <span className="text-gray-500">
            You can plan for the best but sometimes, life has other plans. That’s
            why we created our ‘Ship with Confidence’ Guarantee
          </span>
        </div>
        <div className="flex flex-col  gap-[2.4rem]">
          <TruckIcon />
          <strong>Location sharing & tracking</strong>
          <span className="text-gray-500">
            We want you to have a peace of mind. Easily get status updates and
            keep an eye on your bike at every turn.
          </span>
        </div>
      </div> */}
  </div>
);

const Footer = ({ setShowModal }: { setShowModal: (val: boolean) => void }) => (
  <div className="relative flex h-[40rem] w-full flex-col gap-[2.4rem] bg-black px-[1rem] sm:px-[12rem] ">
    <SafeImage
      alt={'ship my dirt bike banner'}
      src={
        'https://cdn.mxlocker.com/assets/ship-my-dirt-bike/ship-dirt-bike-footer.webp'
      }
      fill
      className="object-cover"
    />
    <div className="z-10 flex h-full flex-col  items-center justify-center gap-[3.2rem] text-white">
      <h1 className="text-center text-[2.4rem] font-semibold sm:text-start sm:text-[3.2rem]">
        Ready to ship your dirt bike with MX Locker?
      </h1>
      <Button
        type="secondary"
        onClick={() => setShowModal(true)}
        text="Get Started"
        width="fixed"
      />
    </div>
  </div>
);

const CompareIcon = () => (
  <svg
    width="110"
    height="110"
    viewBox="0 0 110 110"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M54.7523 16.7402C51.7532 16.7402 49.3151 19.1783 49.3151 22.1774C49.3151 25.1752 51.7543 27.6145 54.7523 27.6145C57.7499 27.6145 60.1894 25.1748 60.1894 22.1774C60.1894 19.1787 57.7508 16.7402 54.7523 16.7402ZM28.0585 81.986C31.0626 81.9833 33.4993 79.5459 33.4993 76.5489C33.4993 73.551 31.0601 71.1117 28.0622 71.1117C25.0639 71.1117 22.625 73.5508 22.625 76.5489C22.625 79.5456 25.0621 81.9835 28.0585 81.986ZM86.8787 76.5489C86.8787 73.5518 84.4395 71.1117 81.4424 71.1117C78.4441 71.1117 76.0052 73.5506 76.0054 76.5489C76.0054 79.5459 78.4419 81.9835 81.4387 81.986C84.4417 81.9833 86.8787 79.5448 86.8787 76.5489Z"
      fill="#C41719"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M71.5761 92.0468C72.7111 87.4644 76.8327 84.162 81.6872 84.1606H81.6902H81.6932C86.5455 84.162 90.6671 87.4644 91.8021 92.0468H71.5761ZM81.6902 72.3284C84.2463 72.3284 86.3282 74.4102 86.3282 76.9671C86.3282 79.5232 84.2478 81.6037 81.6932 81.6059H81.6902C81.688 81.6059 81.6872 81.6059 81.6872 81.6059C79.1303 81.6037 77.0514 79.5232 77.0514 76.9671C77.0514 74.4102 79.1317 72.3284 81.6902 72.3284ZM94.5918 93.1884C94.0581 88.2047 90.7693 84.1491 86.3304 82.4565C87.89 81.136 88.8835 79.1654 88.8835 76.967C88.8835 73.0001 85.6563 69.7737 81.6902 69.7737C77.7234 69.7737 74.4968 73.0003 74.4968 76.967C74.4968 79.1654 75.4896 81.136 77.0484 82.4565C72.6088 84.1491 69.3199 88.2045 68.7863 93.1884C68.7479 93.5491 68.8645 93.9083 69.1056 94.1782C69.3485 94.4481 69.6948 94.6013 70.0557 94.6013H93.3223C93.6846 94.6013 94.0295 94.4464 94.2724 94.1782C94.5151 93.9085 94.6315 93.5476 94.5918 93.1884ZM18.1961 92.0468C19.3333 87.4644 23.4542 84.162 28.3071 84.1606H28.3101H28.3132C33.1661 84.162 37.287 87.4644 38.4242 92.0468H18.1961ZM28.31 72.3284C30.8677 72.3284 32.9487 74.4102 32.9487 76.9671C32.9487 79.5232 30.8699 81.6037 28.313 81.6059H28.31H28.3069C25.7508 81.6037 23.6712 79.5232 23.6712 76.9671C23.6712 74.4102 25.7516 72.3284 28.31 72.3284ZM32.9502 82.4566C34.5104 81.1361 35.5032 79.1656 35.5032 76.9671C35.5032 73.0003 32.276 69.7738 28.3098 69.7738C24.3438 69.7738 21.1165 73.0004 21.1165 76.9671C21.1165 79.1656 22.1094 81.1361 23.6696 82.4566C19.2308 84.1492 15.9397 88.2047 15.4068 93.1885C15.3677 93.5492 15.4849 93.9085 15.7278 94.1784C15.9705 94.4482 16.3155 94.6015 16.6777 94.6015H39.9421C40.305 94.6015 40.6515 94.4466 40.8922 94.1784C41.1349 93.9085 41.2521 93.5478 41.2131 93.1885C40.6803 88.2047 37.389 84.1492 32.9502 82.4566ZM45.8586 60.9623H48.3125V59.0239H41.4672C40.7614 59.0239 40.1902 58.4504 40.1902 57.747C40.1902 57.0412 40.7614 56.4693 41.4672 56.4693H48.3125V54.6121C48.3125 54.4985 48.3192 54.3866 48.329 54.2769H41.467C40.7612 54.2769 40.1901 53.7049 40.1901 52.9992C40.1901 52.2936 40.7612 51.7222 41.467 51.7222H49.5917C50.2877 51.0864 51.2121 50.7 52.226 50.7H59.1308V46.395C59.1308 45.6464 58.5219 45.0361 57.7734 45.0361H38.4699C37.7213 45.0361 37.1126 45.6464 37.1126 46.395V59.6032C37.1126 60.3526 37.7215 60.9621 38.4699 60.9621H40.7014C41.4063 60.9621 41.9783 61.5341 41.9783 62.2398V64.4336L44.9373 61.3536C45.1784 61.1028 45.5107 60.9623 45.8586 60.9623ZM71.5295 53.2548C72.2789 53.2548 72.8867 53.8637 72.8867 54.6122V67.8205C72.8867 68.5699 72.2787 69.1794 71.5295 69.1794H69.2988C68.593 69.1794 68.0211 69.7513 68.0211 70.4571V72.6511L65.0629 69.5727C64.8216 69.32 64.4895 69.1795 64.1414 69.1795H52.2259C51.4773 69.1795 50.8678 68.57 50.8678 67.8207V54.6124C50.8678 53.8638 51.4773 53.255 52.2259 53.255L71.5295 53.2548ZM52.226 71.7347C50.069 71.7347 48.3125 69.9782 48.3125 67.8205V63.5177H46.4012L41.6219 68.4932C41.3762 68.7487 41.0408 68.8856 40.7012 68.8856C40.5396 68.8856 40.378 68.8555 40.2231 68.7931C39.7392 68.5985 39.4235 68.1287 39.4235 67.6086V63.5177H38.4697C36.3128 63.5177 34.5578 61.7613 34.5578 59.6036V46.3953C34.5578 44.2382 36.3128 42.4818 38.4697 42.4818H57.7734C59.9304 42.4818 61.6853 44.2382 61.6853 46.3953V50.7003H71.5295C73.6866 50.7003 75.4414 52.4553 75.4414 54.6122V67.8205C75.4414 69.9782 73.6866 71.7347 71.5295 71.7347H70.5765V75.8256C70.5765 76.3457 70.2601 76.8154 69.7761 77.0101C69.6212 77.0725 69.4604 77.1025 69.2988 77.1025C68.959 77.1025 68.6239 76.9657 68.3773 76.7102L63.5966 71.7347H52.226ZM40.1902 48.2523C40.1902 47.5465 40.7614 46.9746 41.4672 46.9746H43.5115C44.2171 46.9746 44.7884 47.5465 44.7884 48.2523C44.7884 48.9581 44.2173 49.53 43.5115 49.53H41.4672C40.7614 49.53 40.1902 48.9581 40.1902 48.2523ZM46.6027 48.2523C46.6027 47.5465 47.1747 46.9746 47.8804 46.9746H54.7762C55.482 46.9746 56.054 47.5465 56.054 48.2523C56.054 48.9581 55.482 49.53 54.7762 49.53H47.8804C47.1747 49.53 46.6027 48.9581 46.6027 48.2523ZM53.9028 86.1244C50.5838 86.1244 47.2618 85.7321 44.0075 84.9458L46.1058 87.2149C46.5853 87.7343 46.553 88.5421 46.0358 89.021C45.7893 89.2479 45.4783 89.3592 45.1685 89.3592C44.8236 89.3592 44.4823 89.2223 44.2297 88.9502L38.9491 83.2389C38.6597 82.9277 38.547 82.4918 38.6439 82.0785C38.7431 81.6645 39.0399 81.3261 39.4375 81.1759L46.0243 78.7009C46.6851 78.4521 47.4215 78.7858 47.6703 79.4464C47.9176 80.1064 47.5839 80.8436 46.9232 81.0909L43.8095 82.2618C52.3488 84.5098 61.4325 83.8478 69.6081 80.3424C70.2567 80.0642 71.0075 80.3634 71.2849 81.0127C71.563 81.6613 71.2624 82.4115 70.6146 82.6895C65.2914 84.973 59.6005 86.1244 53.9028 86.1244ZM70.061 26.2235C69.6086 26.7646 69.6822 27.5695 70.2234 28.0213C79.1941 35.4972 84.4379 46.7161 84.4973 58.3504L82.5635 55.6485C82.1531 55.0751 81.3549 54.9429 80.7815 55.3547C80.2081 55.7651 80.0757 56.5617 80.4861 57.1367L84.5829 62.8578C84.8226 63.1937 85.209 63.3914 85.6208 63.3914H85.6575C86.0837 63.3786 86.4752 63.1548 86.7023 62.7955L90.8502 56.217C91.2275 55.6217 91.0487 54.8326 90.4512 54.4561C89.8559 54.0796 89.0668 54.2584 88.6903 54.8551L87.0405 57.469C86.7264 45.3987 81.182 33.8303 71.8602 26.059C71.3176 25.6073 70.5127 25.6809 70.061 26.2235ZM34.0874 30.6015L31.0721 31.2839C30.3843 31.438 29.7004 31.0074 29.5441 30.319C29.3894 29.6327 29.8207 28.9474 30.5084 28.7925L38.0948 27.0744C38.5088 26.9803 38.9425 27.0985 39.2523 27.3907C39.5627 27.6831 39.7063 28.1093 39.6371 28.5294L38.4879 35.4716C38.3841 36.0963 37.8431 36.5397 37.2298 36.5397C37.1593 36.5397 37.0893 36.5352 37.0186 36.5225C36.3225 36.4075 35.8513 35.7498 35.9665 35.0539L36.5098 31.777C28.3259 40.0452 24.1012 51.6857 25.1565 63.3148C25.2204 64.0175 24.7034 64.6383 24.0006 64.7022C23.9607 64.7052 23.9224 64.7075 23.8841 64.7075C23.231 64.7075 22.6732 64.2091 22.6132 63.5463C21.5141 51.4586 25.7741 39.3582 34.0874 30.6015ZM55.0001 29.7891C59.8538 29.7891 63.977 33.0907 65.1142 37.6739L44.8862 37.6755C46.0232 33.0907 50.1456 29.7891 55.0001 29.7891ZM55.0001 17.9569C57.5578 17.9569 59.6389 20.0373 59.6389 22.5956C59.6389 25.1517 57.5578 27.2344 55.0001 27.2344C52.4417 27.2344 50.3613 25.1517 50.3613 22.5956C50.3613 20.0373 52.4417 17.9569 55.0001 17.9569ZM43.3672 40.2298L66.6324 40.2284C66.9945 40.2284 67.3396 40.0751 67.5823 39.8053C67.825 39.5354 67.9416 39.1761 67.9032 38.8154C67.3704 33.8332 64.0791 29.7761 59.6403 28.0851C61.1999 26.7646 62.1934 24.794 62.1934 22.5956C62.1934 18.6296 58.9661 15.4023 54.9999 15.4023C51.0331 15.4023 47.8065 18.6296 47.8065 22.5956C47.8065 24.794 48.7994 26.7646 50.3596 28.0851C45.9207 29.7761 42.6296 33.8332 42.096 38.8154C42.0583 39.1763 42.1749 39.5354 42.4169 39.8053C42.6584 40.0751 43.0049 40.2298 43.3672 40.2298Z"
      fill="black"
    />
    <path
      d="M71.5295 53.2548C72.2789 53.2548 72.8867 53.8637 72.8867 54.6122V67.8205C72.8867 68.5699 72.2787 69.1794 71.5295 69.1794H69.2988C68.593 69.1794 68.0211 69.7513 68.0211 70.4571V72.6511L65.0629 69.5727C64.8216 69.32 64.4895 69.1795 64.1414 69.1795H52.2259C51.4773 69.1795 50.8678 68.57 50.8678 67.8207V54.6124C50.8678 53.8638 51.4773 53.255 52.2259 53.255L71.5295 53.2548Z"
      fill="#C41719"
    />
    <path
      d="M62.6902 56.5627L63.5752 58.8643L65.9469 59.03C66.1226 59.0424 66.2908 59.1089 66.43 59.2213C66.5691 59.3336 66.6729 59.4865 66.728 59.6605C66.7831 59.8344 66.787 60.0216 66.7394 60.1979C66.6917 60.3743 66.5945 60.5318 66.4603 60.6504L64.6283 62.2431L65.2124 64.6368C65.2575 64.8154 65.2498 65.004 65.1905 65.1781C65.1312 65.3521 65.0229 65.5036 64.8797 65.6128C64.7366 65.722 64.5651 65.7839 64.3877 65.7904C64.2103 65.797 64.0352 65.7479 63.885 65.6495L61.8672 64.3238L59.8494 65.6495C59.6991 65.7479 59.524 65.797 59.3466 65.7904C59.1692 65.7839 58.9978 65.722 58.8546 65.6128C58.7115 65.5036 58.6031 65.3521 58.5438 65.1781C58.4845 65.004 58.4768 64.8154 58.5219 64.6368L59.106 62.2431L57.2741 60.6504C57.1399 60.5318 57.0426 60.3743 56.995 60.1979C56.9473 60.0216 56.9513 59.8344 57.0064 59.6605C57.0615 59.4865 57.1652 59.3336 57.3044 59.2213C57.4435 59.1089 57.6117 59.0424 57.7874 59.03L60.1592 58.8643L61.0442 56.5627C61.1094 56.391 61.2227 56.2437 61.3695 56.1398C61.5162 56.036 61.6897 55.9805 61.8672 55.9805C62.0447 55.9805 62.2181 56.036 62.3649 56.1398C62.5116 56.2437 62.625 56.391 62.6902 56.5627Z"
      fill="white"
    />
  </svg>
);

const TrackIcon = () => (
  <svg
    width="110"
    height="110"
    viewBox="0 0 110 110"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M29.2484 83.2871H21.0599C20.248 83.2871 19.5898 83.9453 19.5898 84.7572V92.9457C19.5898 93.7576 20.248 94.4158 21.0599 94.4158H29.2484C30.0603 94.4158 30.7185 93.7576 30.7185 92.9457V84.7572C30.7185 83.9453 30.0603 83.2871 29.2484 83.2871Z"
      fill="#C41719"
    />
    <path
      d="M58.474 83.2871H50.2855C49.4736 83.2871 48.8154 83.9453 48.8154 84.7572V92.9457C48.8154 93.7576 49.4736 94.4158 50.2855 94.4158H58.474C59.2859 94.4158 59.9441 93.7576 59.9441 92.9457V84.7572C59.9441 83.9453 59.2859 83.2871 58.474 83.2871Z"
      fill="#C41719"
    />
    <path
      d="M55.9904 39.6139V39.4816V39.364L55.8728 39.1729L55.7846 39.07V38.9524C55.6963 38.8626 55.5976 38.7836 55.4906 38.7172L35.4238 27.2504C35.2719 27.1704 35.1081 27.1158 34.9386 27.0887H34.8063C34.6404 27.0668 34.4724 27.0668 34.3065 27.0887C34.1883 27.1174 34.0746 27.1619 33.9684 27.221L33.8801 27.2945L33.7772 27.3827C33.6378 27.4881 33.5184 27.6176 33.4244 27.7649C33.3444 27.9167 33.2898 28.0806 33.2627 28.2501C33.2627 28.2501 33.2627 28.3383 33.2627 28.3824C33.2627 28.4265 33.2627 28.4559 33.2627 28.5V51.4776C33.2617 51.7367 33.3291 51.9914 33.4582 52.216C33.5873 52.4406 33.7734 52.6271 33.9978 52.7566L53.8735 64.2381C53.8735 64.2381 53.947 64.2381 53.9764 64.2381H54.1087C54.2666 64.306 54.4367 64.3411 54.6085 64.3411C54.7804 64.3411 54.9505 64.306 55.1084 64.2381H55.2407H55.3436C55.4506 64.1716 55.5493 64.0926 55.6376 64.0028C55.752 63.8813 55.8462 63.7424 55.9169 63.5912V63.4736C55.9818 63.3097 56.0167 63.1354 56.0198 62.9591V39.9961C56.0352 39.8741 56.0352 39.7506 56.0198 39.6286L55.9904 39.6139Z"
      fill="#C41719"
    />
    <path
      d="M33.9528 52.7539L53.8139 64.2207C54.0365 64.3528 54.29 64.4238 54.5489 64.4265C54.8078 64.4238 55.0613 64.3528 55.284 64.2207L75.1744 52.7539C75.3987 52.6244 75.5849 52.4379 75.714 52.2133C75.843 51.9887 75.9105 51.734 75.9094 51.4749V28.5414C75.9105 28.2823 75.843 28.0276 75.714 27.803C75.5849 27.5784 75.3987 27.3919 75.1744 27.2624L55.284 15.7809C55.0605 15.6519 54.807 15.584 54.5489 15.584C54.2909 15.584 54.0373 15.6519 53.8139 15.7809L33.9528 27.2477C33.7285 27.3772 33.5424 27.5637 33.4133 27.7883C33.2842 28.0129 33.2168 28.2676 33.2178 28.5267V51.4749C33.2168 51.734 33.2842 51.9887 33.4133 52.2133C33.5424 52.4379 33.7285 52.6244 33.9528 52.7539ZM36.158 31.0847L53.0788 40.8461V60.4131L36.158 50.5929V31.0847ZM45.1109 24.2046L60.8556 34.6864L54.5783 38.3029L37.6134 28.5414L45.1109 24.2046ZM56.019 60.4131V40.8461L62.1493 37.362V42.6249C62.1493 43.0148 62.3042 43.3888 62.5799 43.6645C62.8556 43.9402 63.2295 44.095 63.6194 44.095C64.0093 44.095 64.3832 43.9402 64.6589 43.6645C64.9346 43.3888 65.0895 43.0148 65.0895 42.6249V35.6273L72.9692 31.07V50.5929L56.019 60.4131ZM54.5489 18.7505L71.4991 28.5414L63.6635 33.0546L47.9482 22.5581L54.5489 18.7505Z"
      fill="black"
    />
    <path
      d="M96.5643 87.3446H89.2138V84.7572C89.2138 84.3673 89.0589 83.9934 88.7832 83.7177C88.5075 83.442 88.1336 83.2871 87.7437 83.2871H79.5259C79.136 83.2871 78.762 83.442 78.4863 83.7177C78.2106 83.9934 78.0558 84.3673 78.0558 84.7572V87.3446H59.9588V84.7572C59.9588 84.3673 59.804 83.9934 59.5283 83.7177C59.2526 83.442 58.8786 83.2871 58.4887 83.2871H50.2856C49.8957 83.2871 49.5218 83.442 49.2461 83.7177C48.9704 83.9934 48.8155 84.3673 48.8155 84.7572V87.3446H30.7186V84.7572C30.7186 84.3673 30.5637 83.9934 30.288 83.7177C30.0123 83.442 29.6384 83.2871 29.2485 83.2871H21.06C20.6701 83.2871 20.2962 83.442 20.0205 83.7177C19.7448 83.9934 19.5899 84.3673 19.5899 84.7572V87.3446H13.3861C12.9962 87.3446 12.6223 87.4995 12.3466 87.7752C12.0709 88.0509 11.916 88.4248 11.916 88.8147C11.916 89.2046 12.0709 89.5785 12.3466 89.8542C12.6223 90.1299 12.9962 90.2848 13.3861 90.2848H19.5899V92.9163C19.5899 93.3061 19.7448 93.6801 20.0205 93.9558C20.2962 94.2315 20.6701 94.3864 21.06 94.3864H29.2485C29.6384 94.3864 30.0123 94.2315 30.288 93.9558C30.5637 93.6801 30.7186 93.3061 30.7186 92.9163V90.2848H48.8155V92.9163C48.8155 93.3061 48.9704 93.6801 49.2461 93.9558C49.5218 94.2315 49.8957 94.3864 50.2856 94.3864H58.4887C58.8786 94.3864 59.2526 94.2315 59.5283 93.9558C59.804 93.6801 59.9588 93.3061 59.9588 92.9163V90.2848H78.0558V92.9163C78.0558 93.3061 78.2106 93.6801 78.4863 93.9558C78.762 94.2315 79.136 94.3864 79.5259 94.3864H87.7143C88.1042 94.3864 88.4781 94.2315 88.7538 93.9558C89.0295 93.6801 89.1844 93.3061 89.1844 92.9163V90.2848H96.5349C96.9248 90.2848 97.2987 90.1299 97.5744 89.8542C97.8501 89.5785 98.005 89.2046 98.005 88.8147C98.005 88.4248 97.8501 88.0509 97.5744 87.7752C97.2987 87.4995 96.9248 87.3446 96.5349 87.3446H96.5643ZM27.7784 91.4903H22.5301V86.2273H27.7784V91.4903ZM57.0186 91.4903H51.7557V86.2273H57.0186V91.4903ZM86.2442 91.4903H80.9959V86.2273H86.2442V91.4903Z"
      fill="black"
    />
    <path
      d="M23.9552 79.2297C24.0926 79.366 24.2555 79.4738 24.4346 79.5469C24.6138 79.6201 24.8055 79.6572 24.999 79.656H25.0725C25.2796 79.6462 25.4823 79.5926 25.6673 79.4989C25.8523 79.4051 26.0153 79.2733 26.1457 79.1121L33.4962 69.924C33.6175 69.7727 33.7077 69.5989 33.7615 69.4126C33.8154 69.2264 33.8318 69.0313 33.81 68.8386C33.7881 68.6459 33.7283 68.4595 33.6341 68.29C33.5399 68.1206 33.4131 67.9714 33.261 67.8512C32.9563 67.6108 32.569 67.5006 32.1834 67.5447C31.7979 67.5887 31.4454 67.7835 31.2028 68.0864L24.8961 75.9955L22.1029 73.217C21.9663 73.0792 21.8037 72.9699 21.6245 72.8952C21.4454 72.8206 21.2532 72.7822 21.0592 72.7822C20.8651 72.7822 20.6729 72.8206 20.4938 72.8952C20.3146 72.9699 20.152 73.0792 20.0154 73.217C19.7416 73.4924 19.5879 73.8651 19.5879 74.2534C19.5879 74.6418 19.7416 75.0144 20.0154 75.2898L23.9552 79.2297Z"
      fill="black"
    />
    <path
      d="M60.9422 68.0837L54.6501 75.9928L51.8716 73.2143C51.5904 72.9735 51.2287 72.8476 50.8587 72.8619C50.4887 72.8762 50.1377 73.0296 49.8759 73.2914C49.6141 73.5532 49.4607 73.9042 49.4464 74.2742C49.4321 74.6442 49.558 75.0059 49.7988 75.2871L53.7387 79.227C54.0125 79.4986 54.3821 79.6517 54.7677 79.6533H54.8559C55.0631 79.6435 55.2658 79.5899 55.4507 79.4962C55.6357 79.4024 55.7987 79.2706 55.9291 79.1094L63.2796 69.9213C63.5233 69.6152 63.6354 69.2249 63.5913 68.8361C63.5472 68.4474 63.3505 68.0921 63.0444 67.8484C62.7383 67.6048 62.348 67.4926 61.9593 67.5368C61.5705 67.5809 61.2152 67.7776 60.9716 68.0837H60.9422Z"
      fill="black"
    />
  </svg>
);

const ShipMyDirtBikeFormSchema = z.object({
  year: z.string().min(1, 'Please select a year'),
  make: z.string().min(1, 'Please select a make'),
  model: z.string().min(1, 'Please select a model'),
  pickupZip: z.string().min(1, 'Please enter a zip code'),
  dropoffZip: z.string().min(1, 'Please enter a zip code'),
});
type ShipMyDirtBikeForm = z.infer<typeof ShipMyDirtBikeFormSchema>;
const GetAQuoteModal = ({
  isOpen,
  dismiss,
}: {
  isOpen: boolean;
  dismiss: () => void;
}) => {
  const { userDoc } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const params = useSearchParams();

  const { control, watch, register, handleSubmit } =
    useForm<ShipMyDirtBikeForm>({
      resolver: zodResolver(ShipMyDirtBikeFormSchema),
      defaultValues: {
        year: params?.get('year') || '',
        make: params?.get('make') || '',
        model: params?.get('model') || '',
        pickupZip: params?.get('pickupZip') || '',
        dropoffZip: params?.get('dropoffZip') || '',
      },
    });
  const form = watch();
  const { bikeYears, bikeMakes, bikeModels } = useBikes(
    [form.year],
    [form.make as Make]
  );

  const getEstimateMutation = useMutation({
    mutationKey: ['getEstimate', form],
    mutationFn: () =>
      getDirtBikeShipping({
        pickupZip: form.pickupZip,
        zipCode: form.dropoffZip,
      }),
    onSuccess: () => {
      setCurrentStep(1);
    },
    onError: (error) => {
      alert(error);
    },
  });
  const quote = getEstimateMutation.data;

  async function submit() {
    getEstimateMutation.mutate();
  }

  const steps: ProcessStep[] = [
    {
      id: 'info',
      title: 'Bike Info',
      content: (
        <form
          className="flex flex-col gap-[2.4rem] p-4"
          onSubmit={handleSubmit(submit)}
        >
          <div className="grid grid-cols-3 gap-[2.4rem]">
            <div className="w-[20rem]">
              <FormLabel value="Year">
                <FormSelect
                  options={bikeYears.map((y) => ({
                    label: `${y}`,
                    value: `${y}`,
                    id: `${y}`,
                  }))}
                  control={control}
                  name="year"
                  placeholder="Select Year"
                />
              </FormLabel>
            </div>

            <div className="w-[20rem]">
              <FormLabel value="Make">
                <FormSelect
                  options={bikeMakes.map((make) => ({
                    label: make,
                    value: make,
                    id: make,
                  }))}
                  control={control}
                  name="make"
                  placeholder="Select Make"
                />
              </FormLabel>
            </div>
            <div className="w-[20rem]">
              <FormLabel value="Make">
                <FormSelect
                  options={bikeModels.map((bike) => ({
                    label: bike,
                    value: bike,
                    id: bike,
                  }))}
                  control={control}
                  name="model"
                  placeholder="Select Model"
                  disabled={!form.make || !form.year}
                />
              </FormLabel>
            </div>
          </div>
          <FormLabel value="Pick up Location">
            <FormInput
              {...register('pickupZip')}
              placeholder="Enter Zip Code"
            />
          </FormLabel>
          <FormLabel value="Drop off Location">
            <FormInput
              {...register('dropoffZip')}
              placeholder="Enter Zip Code"
            />
          </FormLabel>
          {userDoc ? (
            <Button
              text="Get A Quote"
              buttonType="submit"
              loading={getEstimateMutation.isPending}
            />
          ) : (
            <Button
              text="Sign up to get a quote"
              href={`/signup?${encodeQueryData({
                redirect: `/ship-my-dirt-bike?${encodeQueryData({
                  year: form.year,
                  make: form.make,
                  model: form.model,
                  pickupZip: form.pickupZip,
                  dropoffZip: form.dropoffZip,
                  fromSignup: 'true',
                })}`,
              })}`}
            />
          )}
        </form>
      ),
    },
    {
      id: 'quote',
      title: 'Estimated Shipping Cost',
      content: (
        <div className="flex flex-col  gap-[2.4rem]">
          <div className="grid w-fit grid-cols-6 gap-[1.6rem]">
            <span>Year:</span>
            <strong>{form.year}</strong>
            <span>Make:</span>
            <strong>{form.make}</strong>
            <span>Model:</span>
            <strong>{form.model}</strong>
            <span>From:</span>
            <strong>{form.pickupZip}</strong>
            <span>To:</span>
            <strong>{form.dropoffZip}</strong>
          </div>

          {quote && (
            <strong className="flex w-full justify-center text-[2.4rem]">
              Cost: {formatCurrency(quote.shipping_cost)}
            </strong>
          )}
          {userDoc?.roles?.bike_shipper ? (
            <>
              <Button
                text="Continue to book"
                type="secondary"
                href={`/dashboard/bike-shipments/create?${encodeQueryData({
                  year: form.year,
                  make: form.make,
                  model: form.model,
                })}`}
              />
              <Button
                type="tertiary"
                text="Cancel"
                onClick={() => setCurrentStep(0)}
              />
            </>
          ) : (
            <div className="flex flex-col gap-[0.8rem] text-center">
              <span>
                Our booking feature is currently in beta. Please request access
                and we will get back to you.
              </span>
              <Button
                text="Request Access to Ship My Dirt Bike"
                href="mailto:support@mxlocker.com?subject=Request Ship My Dirt Bike Access&body=Hi, I would like to try Ship My Dirt Bike!"
              />
            </div>
          )}
        </div>
      ),
    },
  ];
  return (
    <BaseModal
      isOpen={isOpen}
      isFullScreen={isMobile()}
      dismiss={dismiss}
      title={steps[currentStep].title}
    >
      <div className="h-full w-full p-8">
        <BProcess steps={steps} currentStep={currentStep}></BProcess>
      </div>
    </BaseModal>
  );
};

export default ShipMyDirtBike;
